import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ActionSheet, Button, Card, Dialog, List, NavBar, Popup, Skeleton, Toast } from 'antd-mobile';
import dayjs from 'dayjs';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {DeliveringPath, DeliveryDetailPath} from "../../../enums/DeliveryNavigatePathEnum";
import EmptyState from "../../../components/EmptyState";
import {useCustomToast} from 'hooks/useCustomToast';
import {useLongPress} from 'hooks/useLongPress';
import { useDeliveryTaskOrder, useDeliveryTaskOrderList, useExceptionDictionaries } from "../store/deliveryStore";
import { MapBoxPageMarker } from '../../../constants/mapBoxTrackLog';
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {formatLocalizedDateTime} from "../../../utils/dateFormatUtils";
import {ExceptionDictionaryType, ExceptionSubReasonType} from '../graphql/queries/getExceptionDictionaryForDelivery';
import {t} from "../../../i18n";
import {useTranslation} from "react-i18next";
import { copyToClipboard } from 'utils/copyToClipboard';
import { useNetwork } from 'hooks/useNetwork';
import { getExceptionTypeText, getLocalizedText } from '../../../utils/i18nUtils';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import { getOrderNoteContent } from '../../../utils/orderNoteUtils';
import MapboxDetailViewTemporary from "./MapboxDetailViewTemporary";
import AccessInfoDisplay, { AccessCode } from '../delivering/components/AccessInfoDisplay';
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import DeliveryPhoneAndNavigateTemporary from "./DeliveryPhoneAndNavigateTemporary";
import AccessCodeValidationDialog from '../delivering/components/AccessCodeValidationDialog';
import { useAccessCodes } from '../delivering/hooks/useAccessCodes';
import { validateAccessCodeLocation } from '../delivering/utils/accessCodeLocationValidation';
import {getUserInfo} from "../../../utils/auth";
import {useLazyQuery} from '@apollo/client';
import {ANALYZE_ADDRESS_SIMPLE, AnalyzeAddressSimpleResponse} from '../../../graphql/queries/addressQueries';
import AddressLocationIncorrectButton from "../delivering/components/AddressLocationIncorrectButton";
import { useBatchConfigEnable } from '../../../graphql/queries/commonConfigQueries';
import {
    GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK,
    GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE
} from '../../../constants/grayConfigKeys';
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import { RightOutline, ExclamationCircleOutline, CheckOutline } from 'antd-mobile-icons';
import { isSmsTemplateGrayEnabled } from '../../../utils/gray/SmsTemplateModelGray';

const OrderDetailTemporaryPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const deliveryTaskOrderId = location.state?.deliveryTaskOrderId;
    const trackingNumber = location.state?.trackingNumber;
    const activeTab = location.state?.activeTab;
    const fromMapMode = location.state?.fromMapMode || false;
    const {showToast} = useCustomToast();
    const { t: translate, i18n } = useTranslation();
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const { deliveryTaskOrderList, loading: loadingAll } = useDeliveryTaskOrderList();
    const exceptionDictionaries = useExceptionDictionaries();
    const [failedActionSheetVisible, setFailedActionSheetVisible] = useState(false);
    const [selectedExceptionForSubReasons, setSelectedExceptionForSubReasons] = useState<ExceptionDictionaryType | null>(null);
    const [selectedSubReasonCodes, setSelectedSubReasonCodes] = useState<Set<string>>(new Set());
    const [addAccessInfoDialogVisible, setAddAccessInfoDialogVisible] = useState(false);
    const { isOnline } = useNetwork();
    const [isValidatingLocation, setIsValidatingLocation] = useState(false);
    const [expandedOrderIds, setExpandedOrderIds] = useState<Record<number, boolean>>({});
    const [isSmsEnabled, setIsSmsEnabled] = useState(false);
    const [isCheckingSms, setIsCheckingSms] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const address = useMemo(() => {
        const passedAddress = location.state?.displayAddress;
        if (passedAddress) {
            return passedAddress;
        }
        if (!deliveryTaskOrder) return '';
        return deliveryTaskOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString || [
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
            'US'
        ].filter(Boolean).join(' ');
    }, [deliveryTaskOrder, location.state]);

    // 与派送中列表页 DeliveringTemporaryPage 的「派送中」过滤条件一致（用 store 原始字段）
    const isDeliveringOrder = (o: { isPickedUp?: boolean; isAbnormal?: boolean; isDelivery?: boolean; orderWithAddress?: { order?: { isDelivered?: boolean; isFinished?: boolean } } }) =>
        !!(o.isPickedUp && !o.isAbnormal && o.isDelivery && !o.orderWithAddress?.order?.isDelivered && !o.orderWithAddress?.order?.isFinished);

    const sameAddressOrders = useMemo(() => {
        if (!deliveryTaskOrder || !deliveryTaskOrderList.length) return [];

        const filterBySamePhaseAsCurrent = (orders: typeof deliveryTaskOrderList) => {
            const cur = deliveryTaskOrder;
            if (!isDeliveringOrder(cur)) return orders;
            return orders.filter(o => isDeliveringOrder(o));
        };

        const passedIdList = location.state?.orderIdList;
        if (passedIdList && passedIdList.length > 0) {
            // 优先使用列表页传入的 ID 列表（最准确）
            // 统一转为 String 进行比较，避免数字/字符串混用导致 includes 失败
            const stringIdList = passedIdList.map((id: any) => String(id));
            const matched = deliveryTaskOrderList.filter(o => stringIdList.includes(String(o.id)));
            return filterBySamePhaseAsCurrent(matched);
        }

        // 兜底逻辑：如果 state 丢失（例如直接刷新页面），按已对齐的逻辑匹配
        const currentAddress = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString || address;

        const matchedByAddress = deliveryTaskOrderList.filter(o => {
            // 基础匹配：地址 + 日期 + 分组Key
            const oAddress = o.orderWithAddress?.deliveryAddress?.standardizedAddressString || [
                o.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                o.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                o.orderWithAddress?.deliveryAddress?.city || '',
                o.orderWithAddress?.deliveryAddress?.state || '',
                o.orderWithAddress?.deliveryAddress?.zipcode || '',
                'US'
            ].filter(Boolean).join(' ');

            return (oAddress === currentAddress) && 
                   (o.taskDate === deliveryTaskOrder.taskDate) && 
                   (o.addressGroupKey === deliveryTaskOrder.addressGroupKey);
        });
        return filterBySamePhaseAsCurrent(matchedByAddress);
    }, [deliveryTaskOrder, deliveryTaskOrderList, address, location.state?.orderIdList]);

    const isAggregated = sameAddressOrders.length > 1;
    const singleOrder = sameAddressOrders.length == 1;

    // 根据是否聚合选择显示地址
    const displayAddress = useMemo(() => {
        if (!deliveryTaskOrder) return '';
        
        if (isAggregated) {
            // 聚合订单：优先显示标准地址
            return deliveryTaskOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString || [
                deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
                deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
                deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
                'US'
            ].filter(Boolean).join(' ');
        }
        
        // 单条订单：只显示拼接地址
        return [
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
            deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
            'US'
        ].filter(Boolean).join(' ');
    }, [deliveryTaskOrder, isAggregated]);

    // 长按复制地址
    const { handleLongPressStart, handleLongPressEnd } = useLongPress(async () => {
        if (!displayAddress || displayAddress.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(displayAddress);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    }, 500);

    const toggleOrderExpansion = (orderId: number) => {
        setExpandedOrderIds(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    // 处理多级聚合数据
    const groupedData = useMemo(() => {
        if (!isAggregated) return [];
        return processOrders(sameAddressOrders);
    }, [isAggregated, sameAddressOrders]);

    function processOrders(orders: any[]) {
        const dateGroups = new Map<string, any[]>();
        orders.forEach(order => {
            const date = dayjs(timestampToDate(order.taskDate)).format('YYYY-MM-DD');
            if (!dateGroups.has(date)) {
                dateGroups.set(date, []);
            }
            dateGroups.get(date)!.push(order);
        });

        const sortedDates = Array.from(dateGroups.keys()).sort((a, b) => dayjs(a).isBefore(dayjs(b)) ? -1 : 1);

        return sortedDates.map(date => {
            const dateOrders = dateGroups.get(date)!;
            const recipientGroups = new Map<string, any[]>();
            dateOrders.forEach(order => {
                const key = `${order.fullName || order.orderWithAddress?.deliveryAddress?.fullName}_${order.phoneNumber || order.orderWithAddress?.deliveryAddress?.phoneNumber}`;
                if (!recipientGroups.has(key)) {
                    recipientGroups.set(key, []);
                }
                recipientGroups.get(key)!.push(order);
            });

            const recipients = Array.from(recipientGroups.entries()).map(([key, orders]) => {
                const sortedOrders = [...orders].sort((a, b) => (a.stopNumber || 0) - (b.stopNumber || 0));
                return {
                    key,
                    fullName: sortedOrders[0].fullName || sortedOrders[0].orderWithAddress?.deliveryAddress?.fullName,
                    phone: sortedOrders[0].phoneNumber || sortedOrders[0].orderWithAddress?.deliveryAddress?.phoneNumber,
                    extension: sortedOrders[0].extension || sortedOrders[0].orderWithAddress?.deliveryAddress?.extension,
                    orders: sortedOrders
                };
            });

            recipients.sort((a, b) => (a.orders[0].stopNumber || 0) - (b.orders[0].stopNumber || 0));

            let dateLabel = '';
            const today = dayjs().format('YYYY-MM-DD');
            const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            if (date === today) dateLabel = `(${t('pages.delivery.delivering.today')})`;
            else if (date === yesterday) dateLabel = `(${t('pages.delivery.delivering.yesterday')})`;

            return {
                date,
                dateLabel,
                recipients
            };
        });
    }

    useEffect(() => {
        async function checkSmsGrayStatus() {
            const userInfo = getUserInfo();
            const dspId = deliveryTaskOrder?.dspId;
            const driverId = userInfo?.account?.id;

            if (!dspId || !driverId) {
                setIsSmsEnabled(false);
                setIsCheckingSms(false);
                return;
            }

            try {
                const result = await isSmsTemplateGrayEnabled(dspId, parseInt(driverId));
                setIsSmsEnabled(result);
            } catch (error) {
                console.error('Failed to check SMS gray status:', error);
                setIsSmsEnabled(false);
            } finally {
                setIsCheckingSms(false);
            }
        }

        checkSmsGrayStatus();
    }, [deliveryTaskOrder?.dspId]);

    const handleRecipientSms = (recipient: any) => {
        const firstOrder = recipient.orders[0];
        const userInfo = getUserInfo();
        if (!firstOrder || !userInfo) return;

        navigate(DeliveringPath.DELIVERY_CHAT, {
            state: {
                stopNumber: firstOrder.stopNumber,
                fullName: recipient.fullName,
                phone: recipient.phone,
                tracking: firstOrder.trackingNumber,
                subStopNumber: firstOrder.subStopNumber,
                driverId: parseInt(userInfo.account.id),
                orderAddressId: firstOrder.orderWithAddress?.deliveryAddress?.id || firstOrder.addressId,
                driverName: userInfo.account.name
            }
        });
    };

    // 处理新增Code按钮点击（先校验地址）
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleAddCodeClick = async () => {
        const addressLat = deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined;
        const addressLon = deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined;

        // 延迟显示loading（200ms），如果响应很快则不显示，避免闪烁
        loadingTimeoutRef.current = setTimeout(() => {
            setIsValidatingLocation(true);
        }, 200);

        try {
            await validateAccessCodeLocation({
                addressLat,
                addressLon,
                onValidationPassed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    setAddAccessInfoDialogVisible(true);
                },
                onValidationFailed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                },
                isFormSubmit: false
            });
        } catch (error) {
            // 清除延迟显示loading的定时器
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
            setIsValidatingLocation(false);
            console.error('位置校验失败:', error);
        }
    };
    const [geocodedCoordinates, setGeocodedCoordinates] = useState<{latitude: number, longitude: number} | null>(null);
    const noteWrapperCollapsedRef = useRef<HTMLDivElement | null>(null);
    const noteTextCollapsedRef = useRef<HTMLSpanElement | null>(null);
    const [showNoteIndicator, setShowNoteIndicator] = useState(false);

    // 地址分析相关状态
    const [analyzedAddressLine1, setAnalyzedAddressLine1] = useState<string | undefined>(undefined);
    const [analyzeAddress, { loading: analyzingAddress }] = useLazyQuery<AnalyzeAddressSimpleResponse>(ANALYZE_ADDRESS_SIMPLE);

    // 批量检查灰度配置
    const { isEnabled: getConfigEnabled, loading: loadingGrayConfigs } = useBatchConfigEnable([
        GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK,
        GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE
    ]);

    // 获取各个灰度配置的启用状态
    const isAddressCoordinateFeedbackEnabled = getConfigEnabled(GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK);
    const isNavigationAddressAnalyzeEnabled = getConfigEnabled(GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE);

    // 门禁码管理Hook
    const {
        accessCodes,
        pendingAccessCodes,
        validationDialogVisible,
        setValidationDialogVisible,
        handleValidationComplete,
        checkPendingAccessCodes,
        isLoadingAccessCodes,
        isFeatureEnabled,
        isCheckingFeature
    } = useAccessCodes({
        orderId: deliveryTaskOrder?.orderWithAddress?.order?.id,
        loading,
        addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
        addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined
    });

    // 当deliveryTaskOrder加载完成后，调用地址分析API
    useEffect(() => {

        if (!deliveryTaskOrder || loading) {
            return;
        }

        const deliveryAddress = deliveryTaskOrder.orderWithAddress?.deliveryAddress;

        if (!deliveryAddress?.addressLine1 || !deliveryAddress?.city || !deliveryAddress?.state || !deliveryAddress?.zipcode) {
            return;
        }

        // 检查灰度配置，如果未启用则不调用地址分析API
        if (!isNavigationAddressAnalyzeEnabled && deliveryTaskOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString) {
            return;
        }

        // 调用地址分析API
        analyzeAddress({
            variables: {
                streetAddress: deliveryAddress.addressLine1,
                unit: deliveryAddress.addressLine2 || null,
                city: deliveryAddress.city,
                state: deliveryAddress.state,
                zipCode: deliveryAddress.zipcode
            }
        }).then(response => {
            if (response.data?.analyzeAddressSimple?.entityComponents) {
                const components = response.data.analyzeAddressSimple.entityComponents;
                // 组合标准化解析后的字段：line1 + city + state + zipcode，state 和 zipcode 之间用空格，其他用逗号分隔
                if (components?.addressLine1) {
                    const stateZip = [components.stateAbbreviation, components.zipcode].filter(Boolean).join(' ');
                    const parts = [components.addressLine1, components.cityName, stateZip].filter(Boolean);
                    setAnalyzedAddressLine1(parts.join(', '));
                }
            }
        }).catch(error => {
            console.error('Failed to analyze address:', error);
            // 分析失败不影响主流程，保持静默
        });

    }, [deliveryTaskOrder, loading, analyzeAddress, isNavigationAddressAnalyzeEnabled]);

    // 处理恢复滚动位置
    useEffect(() => {
        const isReturning = sessionStorage.getItem('returningFromDetail') === 'true';
        if (isReturning && !loading && !loadingAll) {
            const savedScrollTop = sessionStorage.getItem('orderDetailScrollTop');
            if (savedScrollTop && scrollContainerRef.current) {
                const top = parseInt(savedScrollTop, 10);
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop = top;
                    }
                }, 100);
            }
            // 清除标记，防止干扰其他正常进入
            sessionStorage.removeItem('orderDetailScrollTop');
        }
    }, [loading, loadingAll, sameAddressOrders.length]);

    // 处理返回逻辑
    const handleBack = () => {
        // 正常返回
        if (fromMapMode) {
            // 如果来自地图模式，则返回到地图模式
            navigate("/delivery/temporary/map-mode");
        } else {
            // 默认返回到配送页面
            navigate("/delivery/delivering/temporary", { state: { activeTab } });
        }
    };

    const handleDeliverySuccess = async () => {
        // 保存滚动位置
        if (scrollContainerRef.current) {
            sessionStorage.setItem('orderDetailScrollTop', scrollContainerRef.current.scrollTop.toString());
        }
        // 处理单个订单派送成功逻辑
        if (singleOrder){
            const parcels = sameAddressOrders.map((order: any) => ({
                trackingNumber: order.trackingNumber,
                stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
                deliveryTaskOrderId: Number(order.id)
            }));
            //无网络情况
            if (!isOnline) {
                const parcel = parcels[0];
                const selectedItems = [{
                    trackingNumber: parcel.trackingNumber,
                    stopNumber: parcel.stopNumber,
                    deliveryTaskOrderId: parcel.deliveryTaskOrderId
                }];

                const deliveryTaskOrderIdList = parcel.deliveryTaskOrderId
                    ? [parcel.deliveryTaskOrderId]
                    : [];

                // 直接导航到下一页
                navigate("/delivery/temporary/delivery", {
                    state: {
                        scanResults: selectedItems,
                        deliveryTaskOrderIdList: deliveryTaskOrderIdList
                    },
                    replace: true
                });
                return;
            }

            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/delivery",
                    parcels: parcels,
                    activeTab: activeTab,
                    fromMapMode: fromMapMode
                }
            });
            return;
        }

        // 处理多订单聚合派送成功逻辑
        const parcels = sameAddressOrders.map((order: any) => ({
            trackingNumber: order.trackingNumber,
            stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
            deliveryTaskOrderId: Number(order.id)
        }));

        navigate('/delivery/scan-verification-temporary', {
            state: {
                targetPath: "/delivery/temporary/batch-delivery",
                parcels: parcels,
                activeTab: activeTab,
                fromMapMode: fromMapMode
            }
        });
    };

    const handleFailedActionSheetVisible = async () => {
        if (exceptionDictionaries.length === 0){
            showToast('fail', 'No exception actions available.', 'center');
            return;
        }
        setFailedActionSheetVisible(true);
    };
    
    const handleActionClick = async (action: ExceptionDictionaryType) => {
        setFailedActionSheetVisible(false);
        // 保存滚动位置
        if (scrollContainerRef.current) {
            sessionStorage.setItem('orderDetailScrollTop', scrollContainerRef.current.scrollTop.toString());
        }

        if (isAggregated) {
            const parcels = sameAddressOrders.map((order: any) => ({
                trackingNumber: order.trackingNumber,
                stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
                deliveryTaskOrderId: Number(order.id)
            }));

            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/batch-failed",
                    parcels: parcels,
                        exceptionReason: action,
                    activeTab: activeTab,
                    fromMapMode: fromMapMode
                }
            });
        } else {
            const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
            if (!isOnline){
                navigate("/delivery/temporary/mark-exception",{
                    state:{
                        deliveryTaskOrderId: deliveryTaskOrderId,
                        trackingNumber:currentTrackingNumber,
                        exceptionReason: action,
                        activeTab: activeTab,
                        fromMapMode: fromMapMode
                    }
                });
                return;
            }
            const parcels = [
                {
                    trackingNumber: currentTrackingNumber,
                    stopNumber: deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0',
                    deliveryTaskOrderId: deliveryTaskOrderId,
                },
            ];
            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/mark-exception",
                    parcels: parcels,
                    exceptionReason: action,
                    activeTab: activeTab,
                    fromMapMode: fromMapMode
                }
            });
        }
    };

    const handleSubReasonConfirm = (action: ExceptionDictionaryType, subReasons: ExceptionSubReasonType[]) => {
        setSelectedExceptionForSubReasons(null);
        setSelectedSubReasonCodes(new Set());
        if (scrollContainerRef.current) {
            sessionStorage.setItem('orderDetailScrollTop', scrollContainerRef.current.scrollTop.toString());
        }

        if (isAggregated) {
            const parcels = sameAddressOrders.map((order: any) => ({
                trackingNumber: order.trackingNumber,
                stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
                deliveryTaskOrderId: Number(order.id)
            }));

            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/batch-failed",
                    parcels: parcels,
                    exceptionReason: action,
                    exceptionSubReasons: subReasons,
                    activeTab: activeTab,
                    fromMapMode: fromMapMode
                }
            });
        } else {
            const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
            if (!isOnline) {
                navigate("/delivery/temporary/mark-exception", {
                    state: {
                        deliveryTaskOrderId: deliveryTaskOrderId,
                        trackingNumber: currentTrackingNumber,
                        exceptionReason: action,
                        exceptionSubReasons: subReasons,
                        activeTab: activeTab,
                        fromMapMode: fromMapMode
                    }
                });
                return;
            }
            const parcels = [
                {
                    trackingNumber: currentTrackingNumber,
                    stopNumber: deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0',
                    deliveryTaskOrderId: deliveryTaskOrderId,
                },
            ];
            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/mark-exception",
                    parcels: parcels,
                    exceptionReason: action,
                    exceptionSubReasons: subReasons,
                    activeTab: activeTab,
                    fromMapMode: fromMapMode
                }
            });
        }
    };

    // 处理来自原生的派送成功回调
    const handleDeliverySuccessFromNative = () => {
        handleDeliverySuccess();
    };

    // 处理来自原生的派送失败回调
    const handleDeliveryFailedFromNative = () => {
        handleFailedActionSheetVisible();
    };

    const handleCopyAddress = async () => {
        if (!displayAddress || displayAddress.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(displayAddress);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    };

    // 复制 tracking number 的函数
    const handleCopyTrackingNumber = async (trackingNumber: string) => {
        if (!trackingNumber || trackingNumber.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(trackingNumber);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.trackingNumberCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy tracking number failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    };

    const noteContent = useMemo(() => {
        return getOrderNoteContent(deliveryTaskOrder?.orderNote, i18n.language);
    }, [deliveryTaskOrder?.orderNote, i18n.language]);

    useEffect(() => {
        if (!noteContent) {
            setShowNoteIndicator(false);
            return;
        }

        const measure = () => {
            const wrapper = noteWrapperCollapsedRef.current;
            const text = noteTextCollapsedRef.current;
            if (!wrapper || !text) {
                setShowNoteIndicator(false);
                return;
            }
            // 检测文本是否被截断：文本的实际宽度是否大于容器的可用宽度
            // 考虑指示器大约需要 24px 的空间（图标宽度 + gap）
            const indicatorWidth = 24;
            const availableWidth = wrapper.clientWidth - indicatorWidth;
            const truncated = text.scrollWidth > availableWidth;
            setShowNoteIndicator(truncated);
        };

        // 使用 setTimeout 确保 DOM 已经渲染完成
        const timeoutId = setTimeout(() => {
            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                window.requestAnimationFrame(measure);
            } else {
                measure();
            }
        }, 0);

        if (typeof window !== 'undefined') {
            const handleResize = () => {
                setTimeout(() => {
                    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                        window.requestAnimationFrame(measure);
                    } else {
                        measure();
                    }
                }, 0);
            };
            window.addEventListener('resize', handleResize);
            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', handleResize);
            };
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [noteContent]);

    const handleNoteClick = useCallback(() => {
        if (!noteContent) {
            return;
        }

        const trimmed = noteContent.trim();

        let dialogRef: { close: () => void } | null = null;

        const dialogContent = (
            <div className="flex flex-col max-h-[60vh]">
                <div className="flex-1 overflow-y-auto text-left whitespace-pre-wrap pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {trimmed}
                </div>
                <Button
                    color='primary'
                    block
                    className="mt-4 flex-shrink-0"
                    onClick={() => dialogRef?.close()}
                >
                    {t('pages.delivery.orderDetail.noteDialogButton')}
                </Button>
            </div>
        );

        dialogRef = Dialog.show({
            title: t('pages.delivery.orderDetail.note'),
            closeOnMaskClick: true,
            content: dialogContent,
        });
    }, [noteContent, t]);

    if (loading){
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {trackingNumber || t('pages.delivery.orderDetail.loading')}
                    </NavBar>
                </div>
                <Skeleton style={{width: '100%',height: '45%'}} animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
            </div>
        );
    }

    if (!deliveryTaskOrder) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {trackingNumber || 'Unknown'}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    const stopNumber = deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0';
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phoneNumberParts = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber?.split(',') || [];
    const phone = phoneNumberParts[0] || deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const extension = phoneNumberParts[1] ? phoneNumberParts[1].replace('#', '') : deliveryTaskOrder.orderWithAddress?.deliveryAddress?.extension;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;
    const longitude = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.longitude;
    const latitude = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.latitude;
    const userInfo = getUserInfo();

    return (
        <div className="flex flex-col h-screen relative bg-gray-50">
            {/* 位置校验Loading遮罩 */}
            {isValidatingLocation && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"/>
                            <div className="text-gray-700 text-base">{t('pages.delivery.orderDetail.loading')}</div>
                        </div>
                    </div>
                </div>
            )}
            {/* Navbar */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>
                    {isAggregated ? translate('pages.delivery.temporary.common.delivered') : deliveryTaskOrder?.trackingNumber || trackingNumber}
                </NavBar>
            </div>

            {/* Map */}
            <div className="h-2/5">
                {deliveryTaskOrder && (
                    <MapboxDetailViewTemporary 
                        stopNumber={stopNumber || 1} 
                        address={displayAddress} 
                        longitude={longitude} 
                        latitude={latitude} 
                        pageMarker={MapBoxPageMarker.DELIVERY_ORDER_DETAIL}
                        clusterCount={isAggregated ? sameAddressOrders.length : 1}
                        mapStateKey={deliveryTaskOrderId ? String(deliveryTaskOrderId) : undefined}
                    />
                )}
            </div>

            {/* Address Coordinate Feedback Link - 仅在聚合模式下显示 */}
            {isAggregated && isAddressCoordinateFeedbackEnabled && (
                <div className="px-4 pt-3 flex-none flex justify-end">
                    <span 
                        className="text-red-600 text-sm cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
                        onClick={() => {
                            const targetPath = DeliveringPath.DELIVERY_ADDRESS_COORDINATE_FEEDBACK.replace(':trackingNumber', deliveryTaskOrder?.trackingNumber || trackingNumber);
                            navigate(targetPath, {
                                state: {
                                    deliveryTaskOrderId: deliveryTaskOrderId,
                                    trackingNumber: deliveryTaskOrder?.trackingNumber || trackingNumber,
                                    originalLatitude: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude,
                                    originalLongitude: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude,
                                    address: displayAddress,
                                    stopNumber: deliveryTaskOrder?.stopNumber ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0'
                                }
                            });
                        }}
                    >
                        <ExclamationCircleOutline fontSize={14} />
                        {t('pages.delivery.addressCoordinateFeedback.addressLocationIncorrect')}
                    </span>
                </div>
            )}

            {/* Content Area */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {!isAggregated ? (
                    // 单个订单模式
                    <Card className="flex-1 m-2 rounded-lg overflow-y-auto flex flex-col relative">
                        <div className="flex justify-between items-center mb-4 gap-3 sm:gap-4">
                            <div className="flex flex-col h-full justify-center items-center flex-shrink-0 min-w-[60px] sm:min-w-[80px] w-[60px] sm:w-20">
                                <p className="text-2xl font-bold whitespace-nowrap">{stopNumber}</p>
                                <p className="text-sm text-gray-500">{t('pages.stop')}</p>
                            </div>
                            <div className="flex flex-col gap-3 flex-1 min-w-0 relative">
                                {/* Address location incorrect button - top right (only show for whitelisted drivers) */}
                                {isAddressCoordinateFeedbackEnabled && (
                                    <AddressLocationIncorrectButton
                                        deliveryTaskOrderId={deliveryTaskOrderId}
                                        trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                        address={displayAddress}
                                        latitude={latitude}
                                        longitude={longitude}
                                        geocodedCoordinates={geocodedCoordinates}
                                        stopNumber={stopNumber}
                                        className="absolute -top-3 right-0 text-sm text-red-600 px-0 py-0 z-10"
                                    />
                                )}
                                {/* 第一组：收件人名称；副-手机号 */}
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-medium break-words">{fullName}</h3>
                                    <div className="text-sm text-gray-500">{`${maskPhoneNumber(phone)}${extension ? `-${extension}` : ''}`}</div>
                                </div>
                                {/* 第二组：运单地址；副-门禁信息 */}
                                <div className="flex flex-col gap-1">
                                    <div className="text-blue-500 text-base">
                                        <div className="flex items-start gap-2">
                                            {deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.isAddressChanged && (
                                                <span className="px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded whitespace-nowrap mt-1">
                                                    {t('pages.delivery.orderDetail.addressUpdated')}
                                                </span>
                                            )}
                                            <span className="break-words flex-1 min-w-0 leading-relaxed">{displayAddress}</span>
                                            <button
                                                onClick={handleCopyAddress}
                                                className="p-1 flex items-center justify-center flex-shrink-0"
                                                style={{ minWidth: '24px', height: '24px' }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.isAddressChanged && (
                                            <div className="text-xs text-red-500 mt-1">{t('pages.delivery.orderDetail.addressUpdatedHint')}</div>
                                        )}
                                    </div>
                                    <AccessInfoDisplay
                                        accessCodes={accessCodes}
                                        addressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id}
                                        trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                        address={displayAddress}
                                        addressLat={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined}
                                        addressLon={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined}
                                        orderId={deliveryTaskOrder?.orderWithAddress?.order?.id}
                                        onAddCode={handleAddCodeClick}
                                        showAddButton={true}
                                        isLoading={isLoadingAccessCodes || isCheckingFeature}
                                        fromMapMode={fromMapMode}
                                        deliveryTaskOrderId={deliveryTaskOrderId}
                                        isFeatureEnabled={isFeatureEnabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <DeliveryPhoneAndNavigateTemporary
                            tracking={deliveryTaskOrder?.trackingNumber || trackingNumber}
                            phone={phone || ''}
                            address={displayAddress}
                            addressLine1={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString || analyzedAddressLine1}
                            isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                            longitude={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.longitude}
                            latitude={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.latitude}
                            stopNumber={deliveryTaskOrder.stopNumber.toString()}
                            fullName={fullName}
                            subStopNumber={deliveryTaskOrder?.subStopNumber}
                            driverId={parseInt(getUserInfo()!!.account.id)}
                            driverName={getUserInfo()!!.account.name}
                            onDeliverySuccessFromNative={handleDeliverySuccessFromNative}
                            onDeliveryFailedFromNative={handleDeliveryFailedFromNative}
                            orderAddressId={deliveryTaskOrder.orderWithAddress.deliveryAddress.id}
                            dspId={deliveryTaskOrder.dspId}
                            callText={t('pages.delivery.orderDetail.newCall')}
                        />

                        <List
                            style={{
                                '--border-top': 'none',
                                '--border-bottom': 'none',
                                '--padding-left' : '0px',
                                '--padding-right' : '0px',
                            }}
                        >
                            <List.Item title={`${t('pages.delivery.orderDetail.route')}:`} extra={routeNumber || '-'} />
                            <List.Item title={`${t('pages.delivery.orderDetail.assignedTime')}:`} extra={deliverAt ? formatLocalizedDateTime(timestampToDate(deliverAt)) : ''}/>
                            <List.Item title={`${t('pages.delivery.orderDetail.deliveryAttempt')}:`} extra={`${deliveryAttempts} out of 3`}/>
                            <List.Item
                                className="[&_.adm-list-item-content-main]:p-0"
                                title=""
                                extra={
                                    <OrderNoteItem 
                                        orderNote={deliveryTaskOrder?.orderNote}
                                        currentLanguage={i18n.language}
                                        className="w-full"
                                    />
                                }
                            />
                        </List>
                    </Card>
                ) : (
                    // 集群模式 (聚合)
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        {/* 顶部固定地址区域 */}
                        <div className="px-4 pt-2 flex-none">
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                        <h2 
                                            className="text-lg font-bold text-gray-800 leading-tight break-words cursor-pointer active:opacity-70 transition-opacity"
                                            onTouchStart={handleLongPressStart}
                                            onTouchEnd={handleLongPressEnd}
                                            onMouseDown={handleLongPressStart}
                                            onMouseUp={handleLongPressEnd}
                                            onMouseLeave={handleLongPressEnd}
                                        >
                                            {displayAddress}
                                        </h2>
                                    </div>
                                    <div className="flex-none">
                                        <DeliveryPhoneAndNavigateTemporary
                                            tracking={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                            phone={phone || ''}
                                            address={displayAddress}
                                            addressLine1={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString || analyzedAddressLine1}
                                            isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                            longitude={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude}
                                            latitude={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude}
                                            stopNumber={deliveryTaskOrder?.stopNumber.toString() || '0'}
                                            fullName={fullName}
                                            subStopNumber={deliveryTaskOrder?.subStopNumber}
                                            driverId={parseInt(getUserInfo()!!.account.id)}
                                            driverName={getUserInfo()!!.account.name}
                                            onDeliverySuccessFromNative={handleDeliverySuccessFromNative}
                                            onDeliveryFailedFromNative={handleDeliveryFailedFromNative}
                                            orderAddressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id!}
                                            dspId={deliveryTaskOrder?.dspId!}
                                            onlyShowNavigate={true}
                                        />
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <AccessInfoDisplay
                                        accessCodes={accessCodes}
                                        addressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id}
                                        trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                        address={displayAddress}
                                        addressLat={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined}
                                        addressLon={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined}
                                        orderId={deliveryTaskOrder?.orderWithAddress?.order?.id}
                                        onAddCode={handleAddCodeClick}
                                        showAddButton={true}
                                        isLoading={isLoadingAccessCodes || isCheckingFeature}
                                        fromMapMode={fromMapMode}
                                        deliveryTaskOrderId={deliveryTaskOrderId}
                                        isFeatureEnabled={isFeatureEnabled}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 可滚动的内容区域 */}
                        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-2">
                            {groupedData.map((dateGroup, dateIdx) => (
                                <div key={dateGroup.date} className="mt-2 mb-4">
                                    {dateGroup.recipients.map((recipient, recIdx) => (
                                        <div key={recipient.key} className="bg-white rounded-xl shadow-sm mb-4 border border-gray-100 overflow-hidden">
                                            <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-50">
                                                <div className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                    <span>{dayjs(dateGroup.date).format('MM-DD')}</span>
                                                    <span className="text-gray-500 font-normal">{dateGroup.dateLabel}</span>
                                                </div>
                                            </div>

                                            <div className="p-4 space-y-4">
                                            {recipient.orders.map((order, orderIdx) => {
                                                // TrackingNumber 中间省略显示逻辑
                                                const trackingNumber = order.trackingNumber || '';
                                                const getMiddleEllipsisText = (text: string, maxLength: number = 20) => {
                                                    if (text.length <= maxLength) return text;
                                                    const ellipsis = '...';
                                                    const charsToShow = maxLength - ellipsis.length;
                                                    const frontChars = Math.ceil(charsToShow / 2);
                                                    const backChars = Math.floor(charsToShow / 2);
                                                    return text.substring(0, frontChars) + ellipsis + text.substring(text.length - backChars);
                                                };
                                                const displayTrackingNumber = getMiddleEllipsisText(trackingNumber, 20);

                                                return (
                                                    <div key={order.id} className="flex items-start gap-3 group">
                                                        <div className="text-2xl font-black text-gray-800 w-16 flex-shrink-0 text-center flex items-center justify-center h-12 whitespace-nowrap">
                                                            {formatStopNumberFromDeliveryTaskOrder(order)}
                                                        </div>
                                                        <div className="flex-1 min-w-0 border-b border-gray-50 pb-3 group-last:border-0 group-last:pb-0 overflow-hidden">
                                                            <div 
                                                                className="flex items-center justify-between h-12"
                                                                onClick={() => toggleOrderExpansion(order.id)}
                                                            >
                                                                <div 
                                                                    className="text-lg text-gray-500 font-bold whitespace-nowrap pr-2 flex-1 min-w-0 overflow-hidden cursor-pointer active:opacity-70 transition-opacity" 
                                                                    title={trackingNumber}
                                                                    onTouchStart={(e) => {
                                                                        e.stopPropagation();
                                                                        const timer = setTimeout(() => {
                                                                            handleCopyTrackingNumber(trackingNumber);
                                                                        }, 500);
                                                                        (e.currentTarget as any)._longPressTimer = timer;
                                                                    }}
                                                                    onTouchEnd={(e) => {
                                                                        const timer = (e.currentTarget as any)._longPressTimer;
                                                                        if (timer) {
                                                                            clearTimeout(timer);
                                                                        }
                                                                    }}
                                                                    onMouseDown={(e) => {
                                                                        e.stopPropagation();
                                                                        const timer = setTimeout(() => {
                                                                            handleCopyTrackingNumber(trackingNumber);
                                                                        }, 500);
                                                                        (e.currentTarget as any)._longPressTimer = timer;
                                                                    }}
                                                                    onMouseUp={(e) => {
                                                                        const timer = (e.currentTarget as any)._longPressTimer;
                                                                        if (timer) {
                                                                            clearTimeout(timer);
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        const timer = (e.currentTarget as any)._longPressTimer;
                                                                        if (timer) {
                                                                            clearTimeout(timer);
                                                                        }
                                                                    }}
                                                                >
                                                                    {displayTrackingNumber}
                                                                </div>
                                                                <div className="relative flex-shrink-0 ml-2">
                                                                    {(order.orderWithAddress?.order?.deliveryAttempts || 0) > 0 && (
                                                                        <div className="absolute -top-4 right-0 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold z-10 shadow-sm border border-white">
                                                                            {order.orderWithAddress?.order?.deliveryAttempts}
                                                                        </div>
                                                                    )}
                                                                    <RightOutline 
                                                                        className={`text-gray-300 transition-transform duration-200 ${expandedOrderIds[order.id] ? 'rotate-90' : ''}`} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            {order.orderWithAddress?.deliveryAddress?.isAddressChanged && (
                                                                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded whitespace-nowrap">
                                                                   {t('pages.delivery.orderDetail.deliverToAppAddress')}
                                                                </span>
                                                            )}
                                                            {expandedOrderIds[order.id] && (
                                                                <div className="mt-4 space-y-3">
                                                                    <div className="flex justify-between items-center text-sm">
                                                                        <span className="text-gray-500">{t('pages.delivery.orderDetail.route')}:</span>
                                                                        <span className="text-gray-900 font-medium">{order.routeName || '-'}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center text-sm">
                                                                        <span className="text-gray-500">{t('pages.delivery.orderDetail.assignedTime')}:</span>
                                                                        <span className="text-gray-900 font-medium">
                                                                            {order.orderWithAddress?.order?.deliverAt ? formatLocalizedDateTime(timestampToDate(order.orderWithAddress.order.deliverAt)) : ''}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center text-sm">
                                                                        <span className="text-gray-500">{t('pages.delivery.orderDetail.deliveryAttempt')}:</span>
                                                                        <span className="text-gray-900 font-medium">
                                                                            {`${order.orderWithAddress?.order?.deliveryAttempts || 0} out of 3`}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            </div>

                                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-50">
                                                <div className="text-lg font-bold text-gray-700 flex-1 truncate min-w-0 pr-2">
                                                    {recipient.fullName}
                                                </div>
                                                <div className="flex gap-2 flex-none items-center">
                                                    <Button 
                                                        size='small' 
                                                        shape='rounded'
                                                        className="px-3 py-1.5 flex flex-row items-center justify-center border-none"
                                                        onClick={() => { window.location.href = `tel:${recipient.phone}`; }}
                                                        style={{ 
                                                            paddingLeft: '12px', 
                                                            paddingRight: '12px',
                                                            '--background-color': '#f0f0f0',
                                                            '--text-color': '#4b5563'
                                                        } as React.CSSProperties}
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500 flex-none">
                                                                <path d="M6.62 10.79c1.44 2.83 3.76 5.21 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                                            </svg>
                                                            <span className="text-sm whitespace-nowrap leading-none">{t('pages.delivery.orderDetail.call')}</span>
                                                        </div>
                                                    </Button>
                                                    {isSmsEnabled && !isCheckingSms && (
                                                        <Button 
                                                            size='small' 
                                                            shape='rounded'
                                                            className="px-3 py-1.5 flex flex-row items-center justify-center border-none"
                                                            onClick={() => handleRecipientSms(recipient)}
                                                            style={{ 
                                                                paddingLeft: '12px', 
                                                                paddingRight: '12px',
                                                                '--background-color': '#f0f0f0',
                                                                '--text-color': '#4b5563'
                                                            } as React.CSSProperties}
                                                        >
                                                            <div className="flex items-center gap-1">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500 flex-none">
                                                                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                                                                </svg>
                                                                <span className="text-sm whitespace-nowrap leading-none">{t('pages.delivery.orderDetail.sms')}</span>
                                                            </div>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div {...getBottomNavBarContainerStyle('flex px-4 py-2 pb-4 gap-4 flex-shrink-0 bg-white border-t border-gray-100 mt-auto')}>
                <Button
                    color='default'
                    onClick={handleFailedActionSheetVisible}
                    block
                    className="h-12 text-lg font-bold"
                    style={{
                        '--background-color': '#6B7280',
                        '--text-color': 'white',
                        border: 'none'
                    }}
                >
                    {t('pages.delivery.orderDetail.failed')}
                </Button>
                <Button
                    color='primary'
                    onClick={handleDeliverySuccess}
                    block
                    className="h-12 text-lg font-bold"
                    style={{
                        '--background-color': '#4F46E5',
                        border: 'none'
                    }}
                >
                    {t('pages.delivery.orderDetail.delivered')}
                </Button>
            </div>

            <ActionSheet
                visible={failedActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.delivery.orderDetail.failed')}</h3>
                        <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => setFailedActionSheetVisible(false)}
                actions={exceptionDictionaries.map(action => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getExceptionTypeText(action)}
                            </div>
                        </div>
                    ),
                    key: action.exceptionReasonCode,
                    onClick: () => {
                        setFailedActionSheetVisible(false);
                        const subReasons = action.exceptionSubReasons;
                        if (subReasons && subReasons.length > 0) {
                            setSelectedExceptionForSubReasons(action);
                            setSelectedSubReasonCodes(new Set());
                        } else {
                            handleActionClick(action);
                        }
                    }
                }))}
            />

            <Popup
                visible={selectedExceptionForSubReasons !== null}
                position="bottom"
                onMaskClick={() => {
                    setSelectedExceptionForSubReasons(null);
                    setSelectedSubReasonCodes(new Set());
                }}
                bodyStyle={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
            >
                <div className="p-4 pb-safe">
                    <h3 className="text-center text-gray-800 font-semibold text-base py-3">
                        {selectedExceptionForSubReasons ? t('pages.delivery.orderDetail.wrongAddressReason') : ''}
                    </h3>
                    <div className="relative mb-2">
                    <div
                        className="max-h-60 overflow-y-auto space-y-2 scrollbar-always-visible pr-3.5"
                    >
                        {(selectedExceptionForSubReasons?.exceptionSubReasons ?? []).map(sub => {
                            const checked = selectedSubReasonCodes.has(sub.code);
                            return (
                                <div
                                    key={sub.code}
                                    className={`flex items-center justify-between py-3 px-4 rounded-xl border-2 bg-white active:opacity-90 ${
                                        checked ? 'border-purple-600' : 'border-gray-200'
                                    }`}
                                    onClick={() => {
                                        const multiSelectCodes = ['ERDL001'];
                                        const isMultiSelect = multiSelectCodes.includes(
                                            selectedExceptionForSubReasons?.exceptionReasonCode ?? ''
                                        );
                                        setSelectedSubReasonCodes(prev => {
                                            if (isMultiSelect) {
                                                const next = new Set(prev);
                                                if (checked) next.delete(sub.code);
                                                else next.add(sub.code);
                                                return next;
                                            }
                                            return prev.has(sub.code) ? new Set() : new Set([sub.code]);
                                        });
                                    }}
                                >
                                    <span className={`text-base ${checked ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                                        {getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn)}
                                    </span>
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            checked ? 'bg-purple-600' : 'border-2 border-gray-300 bg-white'
                                        }`}
                                    >
                                        {checked ? (
                                            <CheckOutline className="text-white" style={{ fontSize: 16 }} />
                                        ) : (
                                            <CheckOutline className="text-gray-300" style={{ fontSize: 16 }} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </div>
                    <Button
                        block
                        color="primary"
                        className="w-full"
                        style={{ '--background-color': '#7c3aed', '--border-radius': '8px' } as React.CSSProperties}
                        disabled={selectedSubReasonCodes.size === 0}
                        onClick={() => {
                            if (selectedExceptionForSubReasons) {
                                const subReasons = (selectedExceptionForSubReasons.exceptionSubReasons ?? []).filter(
                                    sub => selectedSubReasonCodes.has(sub.code)
                                );
                                handleSubReasonConfirm(selectedExceptionForSubReasons, subReasons);
                            }
                        }}
                    >
                        {t('pages.delivery.orderDetail.confirm')}
                    </Button>
                </div>
            </Popup>

            {/* 新增门禁信息确认弹窗 */}
            <Dialog
                visible={addAccessInfoDialogVisible}
                content={
                    <div className="py-2">
                        <div className="text-base font-semibold mb-3 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoTitle')}
                        </div>
                        <div className="text-base mb-4 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoConfirm')}
                        </div>
                    </div>
                }
                actions={[
                    [
                        {
                            key: 'cancel',
                            text: t('pages.delivery.orderDetail.cancel'),
                            onClick: () => setAddAccessInfoDialogVisible(false),
                            style: { color: '#4b5563' }
                        },
                        {
                            key: 'confirm',
                            text: t('pages.delivery.orderDetail.confirm'),
                            style: { color: '#60a5fa' },
                            onClick: () => {
                                setAddAccessInfoDialogVisible(false);
                                // 跳转到新增code页面
                                const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
                                navigate(
                                    DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                                        ':trackingNumber',
                                        currentTrackingNumber
                                    ),
                                    {
                                        state: {
                                            addressId: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id,
                                            trackingNumber: currentTrackingNumber,
                                            address: displayAddress,
                                            addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
                                            addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined,
                                            orderId: deliveryTaskOrder?.orderWithAddress?.order?.id // 传递订单ID
                                        }
                                    }
                                );
                            }
                        }
                    ]
                ]}
                onClose={() => setAddAccessInfoDialogVisible(false)}
            />

            {/* 有效性确认弹窗 */}
            <AccessCodeValidationDialog
                visible={validationDialogVisible}
                pendingAccessCodes={pendingAccessCodes}
                onValidationComplete={handleValidationComplete}
                onClose={() => setValidationDialogVisible(false)}
            />
        </div>
    );
};

export default OrderDetailTemporaryPage;
